export default defineNuxtPlugin(() => {
  if (import.meta.server) return

  // Optimize font loading
  const optimizeFonts = () => {
    // Add font-display: swap to all font-face rules
    const styleSheets = document.styleSheets
    for (let i = 0; i < styleSheets.length; i++) {
      try {
        const rules = styleSheets[i].cssRules
        if (!rules) continue
        
        for (let j = 0; j < rules.length; j++) {
          const rule = rules[j]
          if (rule instanceof CSSFontFaceRule) {
            // Use setProperty to avoid TypeScript error
            rule.style.setProperty('font-display', 'swap')
          }
        }
      } catch (e) {
        // Ignore cross-origin stylesheet errors
        continue
      }
    }
  }

  // Lazy load images that are not in viewport
  const lazyLoadImages = () => {
    const images = document.querySelectorAll('img[data-src]')
    
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement
          img.src = img.dataset.src!
          img.removeAttribute('data-src')
          observer.unobserve(img)
        }
      })
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    })

    images.forEach(img => imageObserver.observe(img))
  }

  // Defer non-critical CSS
  const deferNonCriticalCSS = () => {
    const links = document.querySelectorAll('link[rel="stylesheet"][data-defer]')
    links.forEach(link => {
      const newLink = link.cloneNode() as HTMLLinkElement
      newLink.media = 'print'
      newLink.onload = () => {
        newLink.media = 'all'
      }
      link.parentNode?.replaceChild(newLink, link)
    })
  }

  // Preload critical resources
  const preloadCriticalResources = () => {
    const criticalResources = [
      { href: '/_fonts/inter-latin-400-normal.woff2', as: 'font', type: 'font/woff2' },
      { href: '/_fonts/inter-latin-600-normal.woff2', as: 'font', type: 'font/woff2' }
    ]

    criticalResources.forEach(resource => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.href = resource.href
      link.as = resource.as
      if (resource.type) link.type = resource.type
      link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    })
  }

  // Optimize third-party scripts
  const optimizeThirdPartyScripts = () => {
    // Defer Google Analytics
    const gaScript = document.querySelector('script[src*="google-analytics"]')
    if (gaScript) {
      gaScript.setAttribute('defer', 'true')
    }

    // Load social media SDKs on interaction
    let socialSDKsLoaded = false
    const loadSocialSDKs = () => {
      if (socialSDKsLoaded) return
      socialSDKsLoaded = true

      // Load Facebook SDK
      const fbScript = document.createElement('script')
      fbScript.async = true
      fbScript.defer = true
      fbScript.src = 'https://connect.facebook.net/en_US/sdk.js'
      document.body.appendChild(fbScript)

      // Load Twitter/X widgets
      const twitterScript = document.createElement('script')
      twitterScript.async = true
      twitterScript.defer = true
      twitterScript.src = 'https://platform.twitter.com/widgets.js'
      document.body.appendChild(twitterScript)
    }

    // Load social SDKs on first user interaction
    ['scroll', 'click', 'touchstart'].forEach(event => {
      document.addEventListener(event, loadSocialSDKs, { once: true, passive: true })
    })
  }

  // Remove unused CSS (lightweight version)
  const removeUnusedCSS = () => {
    // This is a simplified version - in production, use tools like PurgeCSS
    requestIdleCallback(() => {
      const stylesheets = document.querySelectorAll('style, link[rel="stylesheet"]')
      // Log for debugging
      console.log(`Found ${stylesheets.length} stylesheets`)
    })
  }

  // Optimize animations for performance
  const optimizeAnimations = () => {
    // Reduce motion for users who prefer it
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      document.documentElement.style.setProperty('--animation-duration', '0.01ms')
      document.documentElement.classList.add('reduce-motion')
    }

    // Use will-change for elements that will animate
    const animatedElements = document.querySelectorAll('[data-animate]')
    animatedElements.forEach(el => {
      (el as HTMLElement).style.willChange = 'transform, opacity'
    })
  }

  // Initialize performance optimizations
  const initOptimizations = () => {
    // Run immediately
    optimizeFonts()
    preloadCriticalResources()
    optimizeAnimations()

    // Run after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        lazyLoadImages()
        deferNonCriticalCSS()
        optimizeThirdPartyScripts()
      })
    } else {
      lazyLoadImages()
      deferNonCriticalCSS()
      optimizeThirdPartyScripts()
    }

    // Run when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        removeUnusedCSS()
      })
    } else {
      setTimeout(removeUnusedCSS, 1000)
    }
  }

  // Start optimizations
  initOptimizations()

  // Monitor performance
  if (import.meta.dev) {
    const observer = new PerformanceObserver((list: PerformanceObserverEntryList) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          console.log('LCP:', entry.startTime)
        }
        if (entry.entryType === 'first-input' && 'processingStart' in entry) {
          const delay = (entry as any).processingStart - entry.startTime
          console.log('FID:', delay)
        }
        if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
          console.log('CLS:', (entry as any).value)
        }
      }
    })

    observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] })
  }
})